import {request} from '@/helpers/apiClient';
import {TIER_CONFIG} from '@/utils/constants/apiConfig/tierConfig';
import {optimizeImage} from '@/utils/imageOptimization';

/**
 * Uploads an image for tier configuration
 *
 * @param {File} imageFile - The image file to upload
 * @returns {Promise<Object>} - API response containing imageUploadedRef and optimizedImage
 */
export const uploadTierImage = async (imageFile) => {
  const optimizedImage = await optimizeImage(imageFile);

  const formData = new FormData();
  formData.append('image', optimizedImage);

  const response = await request(TIER_CONFIG.UPLOAD_TIER_IMAGE, {
    data: formData
  });
  return {
    ...response,
    optimizedImage
  };
};

/**
 * Reads a file as base64 data URL for immediate display
 *
 * @param {File} file - The file to read
 * @returns {Promise<string>} - Promise resolving to data URL
 */
export const readFileAsDataUrl = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      resolve(event.target.result);
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsDataURL(file);
  });
};

/**
 * Converts base64 image data to data URL for display
 *
 * @param {string} base64Data - Base64 encoded image data
 * @param {string} mimeType - MIME type (default: 'image/jpeg')
 * @returns {string} - Data URL for use in img src
 */
export const convertBase64ToDataUrl = (base64Data, mimeType = 'image/jpeg') => {
  if (!base64Data) return null;
  return `data:${mimeType};base64,${base64Data}`;
};

// Simple in-memory cache to store uploaded image data URLs
// This helps preserve images when switching between language tabs
const imageCache = new Map();

export const cacheImageDataUrl = (imageId, dataUrl, name) => {
  if (imageId && dataUrl) {
    imageCache.set(String(imageId), {dataUrl, name});
  }
};

export const getCachedImageDataUrl = (imageId) => {
  if (!imageId) return null;
  const {dataUrl} = imageCache.get(String(imageId)) || {};
  return dataUrl || null;
};

export const getCachedImageName = (imageId) => {
  if (!imageId) return null;
  const {name} = imageCache.get(String(imageId)) || {};
  return name || null;
};
